/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import {
  VariantGroupsContainer,
  SectionTitle,
  VariantCard,
  VariantHeader,
  VariantHeaderTitle,
  VariantBody,
  CategoryCard,
  CategoryHeader,
  CategoryTitle,
  RowHeader,
  RowHeaderText,
  Row,
  Cell,
  EmptyState,
} from "./styles";
import {
  MemberChip,
  MembersChipWrap,
  MembersCountPill,
  MembersEmptyState,
} from "../discount_card_variant_members/styles";
import { resolveAssignedGuestSummaries } from "../discount_card_variant_members/utils";
import { groupExclusionItems } from "../discount_card_exclusions/utils";
import {
  ExclusionCategory,
  ExclusionGroup,
  ExclusionItem,
} from "../discount_card_global_details/styles";
import { SPECIAL_PERQ_GROUPS_I18N_NAMESPACE } from "../../pages/discount_cards/shared/specialPerqGroupsI18n";

const normalizeVariants = (variants = []) =>
  Array.isArray(variants) ? variants : [];

const normalizeString = (value, fallback = "-") => {
  const text = value?.toString?.().trim?.() || "";
  return text.length > 0 ? text : fallback;
};

const specialPerqGroupsKey = (key) =>
  `${SPECIAL_PERQ_GROUPS_I18N_NAMESPACE}.${key}`;

const SPECIAL_CONTACT_GROUPS = [
  {
    icon: <CallOutlinedIcon sx={{ color: "rgba(59, 91, 189, 1)", fontSize: 18 }} />,
    titleKey: "special-contact-phone-title",
    phoneKey: "voice_phone_number",
    messageKey: "voice_special_message",
  },
  {
    icon: <ChatBubbleOutlineOutlinedIcon sx={{ color: "rgba(59, 91, 189, 1)", fontSize: 18 }} />,
    titleKey: "special-contact-message-title",
    phoneKey: "message_phone_number",
    messageKey: "message_special_message",
  },
];

const DiscountCardVariantGroupsView = ({
  variants = [],
  t: tOverride,
}) => {
  const nextVariants = normalizeVariants(variants);
  const { t: translatedT } = useTranslation();
  const t = tOverride ?? translatedT;

  const renderExclusions = (variant) => {
    const exclusionItems = Array.isArray(variant?.exclusion_items)
      ? variant.exclusion_items
      : Array.isArray(variant?.exclusionItems)
        ? variant.exclusionItems
        : [];
    const groupedExclusions = groupExclusionItems(exclusionItems);
    const hasFallbackExclusions = Boolean(variant?.exclusions);

    if (groupedExclusions.length === 0 && !hasFallbackExclusions) {
      return (
        <Box sx={{ mt: 1 }}>
          <EmptyState>{t("discount-cards.create.form.empty-exclusions")}</EmptyState>
        </Box>
      );
    }

    if (groupedExclusions.length === 0) {
      return (
        <ExclusionItem sx={{ whiteSpace: "pre-line" }}>
          {variant?.exclusions}
        </ExclusionItem>
      );
    }

    return groupedExclusions.map((group) => (
      <ExclusionGroup key={group.category}>
        {group.category.toLowerCase() !== "general" && (
          <ExclusionCategory>{group.category}</ExclusionCategory>
        )}
        {group.items.map((item, itemIndex) => (
          <ExclusionItem key={`${group.category}-${itemIndex}-${item}`}>
            {item}
          </ExclusionItem>
        ))}
      </ExclusionGroup>
    ));
  };

  if (nextVariants.length === 0) {
    return null;
  }

  return (
    <VariantGroupsContainer data-testid="discount-card-variant-groups-view">
      <SectionTitle variant="h6">
        {t(specialPerqGroupsKey("title"))}
      </SectionTitle>

      {nextVariants.map((variant, index) => {
        const variantName =
          variant?.name?.trim() ||
          t(specialPerqGroupsKey("default-name"), {
            index: index + 1,
          });
        const rules = Array.isArray(variant?.rules) ? variant.rules : [];
        const assignedMembers = resolveAssignedGuestSummaries(
          Array.isArray(variant?.network_guest_ids) ? variant.network_guest_ids : [],
          Array.isArray(variant?.network_guests) ? variant.network_guests : [],
          (networkGuestId) =>
            t("discount-cards.edit.special-perq-groups.members.unknown-member", {
              id: networkGuestId,
            }),
        );
        const hasSpecialContacts = SPECIAL_CONTACT_GROUPS.some(
          (group) =>
            normalizeString(variant?.[group.phoneKey], "") ||
            normalizeString(variant?.[group.messageKey], ""),
        );
        const locations = Array.isArray(variant?.locations)
          ? variant.locations
          : [];

        return (
          <VariantCard
            key={variant?.id ?? `${variantName}-${index}`}
            data-testid={`discount-card-variant-group-view-${index}`}
          >
            <VariantHeader>
              <Box sx={{ alignItems: "center", display: "flex", minWidth: 0 }}>
                <GroupsOutlinedIcon
                  sx={{ color: "rgba(247, 181, 0, 1)", fontSize: 18, mr: 1 }}
                />
                <VariantHeaderTitle noWrap>{variantName}</VariantHeaderTitle>
              </Box>
            </VariantHeader>

            <VariantBody>
              <Box>
                <SectionTitle variant="subtitle1">
                  {t("discount-cards.create.form.rules.rules")}
                </SectionTitle>

                {rules.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      mt: 1,
                    }}
                  >
                    {rules.map((category, categoryIndex) => {
                      const perks = Array.isArray(category?.perks)
                        ? category.perks
                        : [];

                      return (
                        <CategoryCard
                          key={
                            category?.category_name ??
                            `${variantName}-${categoryIndex}`
                          }
                        >
                          <CategoryHeader>
                            <CategoryTitle>
                              {normalizeString(
                                category?.category_name || category?.category,
                                t("discount-cards.create.form.rules.property"),
                              )}
                            </CategoryTitle>
                          </CategoryHeader>

                          <RowHeader>
                            <RowHeaderText>
                              {t("discount-cards.create.form.rules.people")}
                            </RowHeaderText>
                            <RowHeaderText>
                              {t("discount-cards.create.form.rules.discount-section")}
                            </RowHeaderText>
                            <RowHeaderText>
                              {t("discount-cards.create.form.rules.applicability")}
                            </RowHeaderText>
                          </RowHeader>

                          {perks.length > 0 ? (
                            perks.map((perk, perkIndex) => (
                              <Row key={perk?.id ?? `${categoryIndex}-${perkIndex}`}>
                                <Cell>{normalizeString(perk?.label)}</Cell>
                                <Cell>{normalizeString(perk?.discount)}</Cell>
                                <Cell>{normalizeString(perk?.type)}</Cell>
                              </Row>
                            ))
                          ) : (
                            <Box sx={{ p: 2 }}>
                              <EmptyState>
                                {t(specialPerqGroupsKey("empty-categories"))}
                              </EmptyState>
                            </Box>
                          )}
                        </CategoryCard>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ mt: 1 }}>
                    <EmptyState>
                      {t(specialPerqGroupsKey("empty-categories"))}
                    </EmptyState>
                  </Box>
                )}
              </Box>

              <Box>
                <SectionTitle variant="subtitle1">
                  {t("discount-cards.create.form.exclusions")}
                </SectionTitle>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                  {renderExclusions(variant)}
                </Box>
              </Box>

              <Box>
                <SectionTitle variant="subtitle1">
                  {t(specialPerqGroupsKey("location-title"), {
                    defaultValue: "Applies at location",
                  })}
                </SectionTitle>
                <MembersChipWrap sx={{ mt: 1 }}>
                  {locations.length > 0 ? (
                    locations.map((location) => (
                      <MemberChip
                        key={location?.id}
                        data-testid={`discount-card-variant-group-view-${index}-location-${location?.id}`}
                        label={location?.name}
                      />
                    ))
                  ) : (
                    <MemberChip
                      data-testid={`discount-card-variant-group-view-${index}-location-all`}
                      label={t(specialPerqGroupsKey("location-all-locations"), {
                        defaultValue: "All locations",
                      })}
                    />
                  )}
                </MembersChipWrap>
              </Box>

              <Box>
                <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                  <SectionTitle variant="subtitle1">
                    {t("discount-cards.edit.special-perq-groups.members.title")}
                  </SectionTitle>
                  <MembersCountPill>{assignedMembers.length}</MembersCountPill>
                </Box>
                <CategoryCard>
                  <Box sx={{ p: 2 }}>
                    {assignedMembers.length > 0 ? (
                      <MembersChipWrap
                        data-testid={`discount-card-variant-group-view-${index}-assigned-members`}
                      >
                        {assignedMembers.map((guest) => (
                          <MemberChip
                            key={guest.id}
                            data-testid={`discount-card-variant-group-view-${index}-assigned-member-${guest.id}`}
                            label={guest.label}
                          />
                        ))}
                      </MembersChipWrap>
                    ) : (
                      <MembersEmptyState>
                        {t("discount-cards.edit.special-perq-groups.members.empty")}
                      </MembersEmptyState>
                    )}
                  </Box>
                </CategoryCard>
              </Box>

              {hasSpecialContacts && (
                <Box>
                  <SectionTitle variant="subtitle1">
                    {t(specialPerqGroupsKey("special-contact-title"), {
                      defaultValue: "Special contact numbers",
                    })}
                  </SectionTitle>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      mt: 1,
                    }}
                  >
                    {SPECIAL_CONTACT_GROUPS.filter(
                      (group) =>
                        normalizeString(variant?.[group.phoneKey], "") ||
                        normalizeString(variant?.[group.messageKey], ""),
                    ).map((group) => (
                      <CategoryCard key={group.phoneKey}>
                        <CategoryHeader>
                          <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                            {group.icon}
                            <CategoryTitle>
                              {t(specialPerqGroupsKey(group.titleKey), {
                                defaultValue:
                                  group.phoneKey === "voice_phone_number"
                                    ? "Special phone number"
                                    : "Special message number",
                              })}
                            </CategoryTitle>
                          </Box>
                        </CategoryHeader>
                        <Box
                          sx={{
                            display: "grid",
                            gap: 1.5,
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                            p: 2,
                          }}
                        >
                          <Box>
                            <RowHeaderText>
                              {t(
                                specialPerqGroupsKey("special-contact-phone-label"),
                                { defaultValue: "Phone number" },
                              )}
                            </RowHeaderText>
                            <Cell>{normalizeString(variant?.[group.phoneKey])}</Cell>
                          </Box>
                          <Box>
                            <RowHeaderText>
                              {t(
                                specialPerqGroupsKey(
                                  "special-contact-message-label",
                                ),
                                { defaultValue: "Special message" },
                              )}
                            </RowHeaderText>
                            <Cell>{normalizeString(variant?.[group.messageKey])}</Cell>
                          </Box>
                        </Box>
                      </CategoryCard>
                    ))}
                  </Box>
                </Box>
              )}
            </VariantBody>
          </VariantCard>
        );
      })}
    </VariantGroupsContainer>
  );
};

export default DiscountCardVariantGroupsView;
